import React from 'react';
import { AnimatePresence } from 'motion/react';
import { MountableDialog } from '@oysterjs/ui/dialog';
import type { ApolloError } from '@apollo/client';
import { ValidationErrorCode } from '@oysterjs/core/graphql';
import { getValidationErrors, messageOfFailure } from '@oysterjs/core/graphql/errors';

import { client } from '~/api/client';
import {
  AmsLogStatus,
  AmsPushOutcome,
  MatchAmsAccountsForOutboundEmailDocument,
  type MatchAmsAccountsForOutboundEmailQuery,
  type MatchAmsAccountsForOutboundEmailQueryVariables,
  type OutboundEmailAmsLogFieldsFragment,
  PushOutboundEmailToAmsDocument,
  type PushOutboundEmailToAmsInput,
  type PushOutboundEmailToAmsMutation,
  type PushOutboundEmailToAmsMutationVariables
} from '~/api/graphql';
import { PendingChangesSaveToast } from '~/components/settings/pending-changes-save';
import {
  type EmailAmsMatch,
  amsClientLoggedTo
} from '~/components/workspaces/hooks/use-email-ams-match';
import { integrationName } from '~/lib/integrations';

import { filingTargetLabel } from './client-list';
import { ChooseClientDialog, type ChosenClient } from './choose-client-dialog';

import {
  type PushConfirmState,
  PushConfirmDialog,
  type PushedEmailSummary,
  PushFindingClientDialog,
  type PushLookupState
} from './push-confirm-dialog';

/** A sent email being pushed, with where it stands in the AMS when the push starts. */
export type PushableEmail = PushedEmailSummary & {
  id: string;
  toAddresses: string[];
  amsLog: OutboundEmailAmsLogFieldsFragment;
};

/**
 * mustChoose sends the agent to Choose client even when the recipients lead to one
 * client, because Cara refused the client the push named.
 */
type PushStep =
  | { kind: 'findingClient'; mustChoose: boolean; lookup: PushLookupState }
  | { kind: 'choose'; match: EmailAmsMatch }
  | { kind: 'confirm'; client: ChosenClient; state: PushConfirmState };

type PushResult = PushOutboundEmailToAmsMutation['pushOutboundEmailToAms'];

const TARGET_FIELD: keyof PushOutboundEmailToAmsInput = 'target';
const FILING_TARGET_FIELD: keyof PushOutboundEmailToAmsInput = 'filingTarget';
const LOG_AGAIN_ANYWAY_FIELD: keyof PushOutboundEmailToAmsInput = 'logAgainAnyway';

const LOGGED_TOAST_SHOWN_MS = 6_000;

const confirmStep = (pushClient: ChosenClient): PushStep => ({
  kind: 'confirm',
  client: pushClient,
  state: { kind: 'ready' }
});

const findClientStep = (mustChoose: boolean): PushStep => ({
  kind: 'findingClient',
  mustChoose,
  lookup: { kind: 'finding' }
});

/** The server's answer when no AMS logs the agency's emails. */
const LOGGING_OFF = 'Logging emails to the AMS is off.';

/** The clients the sent email's recipients match, or why Cara couldn't say. */
const matchRecipients = async (
  outboundEmailId: string
): Promise<{ match: EmailAmsMatch } | { lookup: PushLookupState }> => {
  try {
    const result = await client.query<
      MatchAmsAccountsForOutboundEmailQuery,
      MatchAmsAccountsForOutboundEmailQueryVariables
    >({
      query: MatchAmsAccountsForOutboundEmailDocument,
      variables: { outboundEmailId },
      fetchPolicy: 'no-cache'
    });
    const refusal = messageOfFailure(result, 'Try again in a few minutes.');
    if (refusal) return { lookup: { kind: 'refused', message: refusal } };
    const match = result.data?.matchAmsAccountsForOutboundEmail;
    return match ? { match } : { lookup: { kind: 'refused', message: LOGGING_OFF } };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return { lookup: { kind: 'unreachable' } };
  }
};

/** The input field Cara refused the push over, and why, when it was a field. */
const refusalOf = (errors: readonly unknown[]) =>
  getValidationErrors({ graphQLErrors: errors } as ApolloError)?.[0];

type LoggedToast = { title: string; description: string };

const PushSteps = (props: {
  email: PushableEmail;
  onAmsLogChange?: (amsLog: OutboundEmailAmsLogFieldsFragment) => void;
  onAmsLogStale: () => void;
  onLogged: (toast: LoggedToast) => void;
  onClose: () => void;
}) => {
  const { email } = props;
  const amsName = integrationName(email.amsLog.integrationType);
  // Every push names its client, the one the dialog shows, so what is logged is what was
  // confirmed even if the row changed since it was read.
  const [step, setStep] = React.useState<PushStep>(() => findClientStep(false));
  const [editingClient, setEditingClient] = React.useState<ChosenClient>();
  const isUnconfirmed = email.amsLog.status === AmsLogStatus.CouldNotConfirm;
  const [isLogAgain, setIsLogAgain] = React.useState(isUnconfirmed);
  // Follows the row, not the outcome: a log-again that fails leaves the row unconfirmed,
  // so trying again must still say to log it again.
  const [logAgainAnyway, setLogAgainAnyway] = React.useState(isUnconfirmed);
  const isMounted = React.useRef(true);
  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  // A lookup that lands after Cancel, while the dialog animates out, is dropped.
  const hasClosed = React.useRef(false);
  const isChoosingClient = React.useRef(false);
  const stepContent = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const content = stepContent.current;
    if (content && !content.contains(document.activeElement)) content.focus();
  }, [step.kind]);
  const close = () => {
    if (isChoosingClient.current || (step.kind === 'confirm' && step.state.kind === 'logging'))
      return;
    hasClosed.current = true;
    props.onClose();
  };
  // Choose client closes itself after a choice, which doesn't end the push.
  const hasChosenClient = React.useRef(false);

  const isFindingClient = step.kind === 'findingClient' && step.lookup.kind === 'finding';
  const mustChoose = step.kind === 'findingClient' && step.mustChoose;
  React.useEffect(() => {
    if (!isFindingClient) return;
    let current = true;
    void matchRecipients(email.id).then((found) => {
      if (!current || hasClosed.current) return;
      if ('lookup' in found) {
        setStep({ kind: 'findingClient', mustChoose, lookup: found.lookup });
        return;
      }
      const { match } = found;
      const knownClient = mustChoose ? null : amsClientLoggedTo(match, match.choice ?? null);
      if (knownClient) {
        setStep(confirmStep(knownClient));
        return;
      }
      hasChosenClient.current = false;
      setStep({ kind: 'choose', match });
    });
    return () => {
      current = false;
    };
  }, [isFindingClient, mustChoose, email.id]);

  const showConfirmState = (pushClient: ChosenClient, state: PushConfirmState) =>
    setStep({ kind: 'confirm', client: pushClient, state });

  const settle = (pushClient: ChosenClient, pushed: PushResult) => {
    const { amsLog } = pushed.email;
    if (amsLog) {
      props.onAmsLogChange?.(amsLog);
      setLogAgainAnyway(amsLog.status === AmsLogStatus.CouldNotConfirm);
    } else {
      props.onAmsLogStale();
    }
    switch (pushed.outcome) {
      case AmsPushOutcome.Logged:
        props.onLogged({
          title: `Logged to ${amsLog?.accountDisplayName ?? pushClient.displayName} in ${amsName}`,
          description: `${email.subject} is now on their record.`
        });
        return;
      case AmsPushOutcome.Failed:
        showConfirmState(pushClient, { kind: 'failed', reason: amsLog?.failureMessage ?? null });
        return;
      case AmsPushOutcome.SignedOut:
        showConfirmState(pushClient, { kind: 'signedOut' });
        return;
      case AmsPushOutcome.Unconfirmed:
        showConfirmState(pushClient, { kind: 'unconfirmed' });
        return;
    }
  };

  const push = async (pushClient: ChosenClient) => {
    showConfirmState(pushClient, { kind: 'logging' });
    try {
      const result = await client.mutate<
        PushOutboundEmailToAmsMutation,
        PushOutboundEmailToAmsMutationVariables
      >({
        mutation: PushOutboundEmailToAmsDocument,
        variables: {
          id: email.id,
          input: {
            target: pushClient.target,
            ...(pushClient.filingTarget ? { filingTarget: pushClient.filingTarget } : {}),
            logAgainAnyway
          }
        }
      });
      if (!isMounted.current) return;
      const pushed = result.data?.pushOutboundEmailToAms;
      if (pushed) {
        settle(pushClient, pushed);
        return;
      }
      const refusal = refusalOf(result.errors ?? []);
      const refusedField = refusal?.field.at(-1);
      if (refusedField === TARGET_FIELD || refusedField === FILING_TARGET_FIELD) {
        setStep(findClientStep(true));
        return;
      }
      if (
        refusedField === LOG_AGAIN_ANYWAY_FIELD &&
        refusal?.code === ValidationErrorCode.Conflict
      ) {
        setIsLogAgain(true);
        setLogAgainAnyway(true);
        showConfirmState(pushClient, { kind: 'ready' });
        props.onAmsLogStale();
        return;
      }
      showConfirmState(pushClient, {
        kind: 'refused',
        message: messageOfFailure(result, 'Try again in a few minutes.') ?? ''
      });
      props.onAmsLogStale();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      // The server finishes a write it started, so the row may have changed.
      if (!isMounted.current) return;
      showConfirmState(pushClient, { kind: 'connectionLost' });
      props.onAmsLogStale();
    }
  };

  let content: React.ReactNode;
  if (step.kind === 'findingClient') {
    content = (
      <PushFindingClientDialog
        contentOnly
        amsName={amsName}
        isLogAgain={isLogAgain}
        lookup={step.lookup}
        onRetry={() => setStep(findClientStep(step.mustChoose))}
        onClose={close}
      />
    );
  } else if (step.kind === 'choose') {
    content = (
      <ChooseClientDialog
        contentOnly
        onChoosingChange={(isChoosing) => {
          isChoosingClient.current = isChoosing;
        }}
        mode="push"
        outboundEmailId={email.id}
        match={step.match}
        currentClient={
          editingClient?.target.integrationId === step.match.integrationId
            ? editingClient
            : undefined
        }
        toAddresses={email.toAddresses}
        onChoose={(chosen) => {
          isChoosingClient.current = false;
          hasChosenClient.current = true;
          setStep(confirmStep(chosen));
          return Promise.resolve(null);
        }}
        onClose={() => {
          if (!hasChosenClient.current) close();
          hasChosenClient.current = false;
        }}
      />
    );
  } else {
    content = (
      <PushConfirmDialog
        contentOnly
        onChangeFilingLocation={() => {
          if (step.state.kind === 'logging') return;
          setEditingClient(step.client);
          setStep(findClientStep(true));
        }}
        amsName={amsName}
        clientName={
          step.client.filingTarget
            ? `${step.client.displayName} › ${filingTargetLabel(step.client.filingTarget)}`
            : step.client.displayName
        }
        email={email}
        isLogAgain={isLogAgain}
        state={step.state}
        onConfirm={() => void push(step.client)}
        onClose={close}
      />
    );
  }
  return (
    <MountableDialog
      onClose={close}
      size={step.kind === 'choose' ? 'xl' : 'md'}
      className="flex max-h-[calc(100dvh-4rem)] flex-col"
    >
      <div ref={stepContent} tabIndex={-1} className="flex min-h-0 flex-1 flex-col outline-none">
        {content}
      </div>
    </MountableDialog>
  );
};

/**
 * Pushes a sent email to the AMS: confirms first, then says how it went. The toast
 * after a successful push outlives the dialog, so keep this mounted.
 */
export const PushEmailToAms = (props: {
  email: PushableEmail | null;
  /** Takes the AMS log a push left, for a screen that doesn't read it from the Apollo cache. */
  onAmsLogChange?: (amsLog: OutboundEmailAmsLogFieldsFragment) => void;
  /** Reads the email's AMS log again, when a push ended without saying where it stands. */
  onAmsLogStale: () => void;
  onClose: () => void;
}) => {
  const [loggedToast, setLoggedToast] = React.useState<LoggedToast | null>(null);
  // Each opening is a fresh push, even of the email whose dialog is still animating out.
  const isOpen = !!props.email;
  const [opening, setOpening] = React.useState({ isOpen, count: 0 });
  if (opening.isOpen !== isOpen) {
    setOpening({ isOpen, count: isOpen ? opening.count + 1 : opening.count });
  }
  React.useEffect(() => {
    if (!loggedToast) return;
    const timer = window.setTimeout(() => setLoggedToast(null), LOGGED_TOAST_SHOWN_MS);
    return () => window.clearTimeout(timer);
  }, [loggedToast]);

  return (
    <>
      <AnimatePresence>
        {props.email && (
          <PushSteps
            key={`${props.email.id}:${opening.count}`}
            email={props.email}
            onAmsLogChange={props.onAmsLogChange}
            onAmsLogStale={props.onAmsLogStale}
            onLogged={(toast) => {
              setLoggedToast(toast);
              props.onClose();
            }}
            onClose={props.onClose}
          />
        )}
      </AnimatePresence>
      <PendingChangesSaveToast
        saved={!!loggedToast}
        messages={{
          successTitle: loggedToast?.title ?? '',
          successDescription: loggedToast?.description ?? ''
        }}
      />
    </>
  );
};
